import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  RefreshControl,
  TouchableOpacity,
  StatusBar,
  ListRenderItemInfo,
  ActivityIndicator,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useFocusEffect } from '@react-navigation/native';
import { StackScreenProps } from '@react-navigation/stack';
import Icon from 'react-native-vector-icons/Ionicons';
import Toast from 'react-native-toast-message';
import { getProfilesFeed } from '../services/api';
import { ProfileCard, THEME } from '../components';
import { useLanguage } from '../context/LanguageContext';
import type { Profile, RootStackParamList } from '../types';

const PREF_LOOKING_FOR = 'pref_looking_for';

type Props = StackScreenProps<RootStackParamList, 'Main'>;


const HomeScreen: React.FC<Props> = ({ navigation }) => {
  const { t } = useLanguage();
  const insets = useSafeAreaInsets();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [hasReachedEnd, setHasReachedEnd] = useState<boolean>(false);
  const [lookingFor, setLookingFor] = useState<'MALE' | 'FEMALE'>('MALE');
  const [prefTotal, setPrefTotal] = useState<number | undefined>(undefined);

  const fetchProfiles = useCallback(async (gender: 'MALE' | 'FEMALE'): Promise<void> => {
    try {
      const res = await getProfilesFeed(gender, 0);
      const { profiles: list, prefTotal: pt, hasMore } = res.data;
      setProfiles(Array.isArray(list) ? list : []);
      setCurrentPage(0);
      setPrefTotal(pt >= 0 ? pt : undefined);
      setHasReachedEnd(!hasMore);
    } catch (e) {
      Toast.show({ type: 'error', text1: 'Failed to load profiles', text2: (e as Error).message });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useFocusEffect(useCallback(() => {
    AsyncStorage.getItem(PREF_LOOKING_FOR).then(stored => {
      const gender: 'MALE' | 'FEMALE' = (stored === 'MALE' || stored === 'FEMALE') ? stored : 'FEMALE';
      setLookingFor(gender);
      fetchProfiles(gender);
    });
  }, [fetchProfiles]));

  const onRefresh = (): void => { setRefreshing(true); fetchProfiles(lookingFor); };

  const loadMore = async (): Promise<void> => {
    if (loadingMore || hasReachedEnd) return;
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const res = await getProfilesFeed(lookingFor, nextPage, prefTotal);
      const { profiles: more, prefTotal: pt, hasMore } = res.data;
      setProfiles(prev => [...prev, ...(Array.isArray(more) ? more : [])]);
      setCurrentPage(nextPage);
      if (pt >= 0) setPrefTotal(pt);
      setHasReachedEnd(!hasMore);
    } catch {
      Toast.show({ type: 'error', text1: 'Failed to load more profiles' });
    } finally {
      setLoadingMore(false);
    }
  };

  const renderProfile = ({ item }: ListRenderItemInfo<Profile>) => (
    <ProfileCard
      profile={item}
      onPress={() => navigation.navigate('ProfileDetail', { profileId: item.id })}
    />
  );

  return (
    <View style={styles.container}>
      <StatusBar barStyle="dark-content" backgroundColor={THEME.bg} />

      <View style={[styles.header, { paddingTop: insets.top + 12 }]}>
        <View>
          <Text style={styles.headerTitle}>Jeevan Sanyog</Text>
          <Text style={styles.headerSub}>{t('home.tagline', 'Matrimonial')}</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity
            onPress={() => navigation.navigate('Preferences')}
            style={styles.headerBtn}>
            <Icon name="options-outline" size={22} color={THEME.primary} />
          </TouchableOpacity>
          <TouchableOpacity
            onPress={() => navigation.navigate('Search' as never)}
            style={styles.headerBtn}>
            <Icon name="search" size={22} color={THEME.primary} />
          </TouchableOpacity>
        </View>
      </View>

      <FlatList<Profile>
        data={profiles}
        keyExtractor={(item: Profile) => item.id}
        renderItem={renderProfile}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={THEME.primary} />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        ListFooterComponent={
          loadingMore ? <ActivityIndicator color={THEME.primary} style={styles.footerLoader} /> : null
        }
        ListEmptyComponent={
          !loading ? (
            <View style={styles.empty}>
              <Icon name="people-outline" size={60} color={THEME.border} />
              <Text style={styles.emptyText}>{t('msg.noProfilesFound')}</Text>
            </View>
          ) : null
        }
        contentContainerStyle={{ paddingBottom: 80 + insets.bottom, paddingTop: 4 }}
      />

    </View>
  );
};

export default HomeScreen;

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: THEME.bg },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingBottom: 12,
    backgroundColor: THEME.bg,
  },
  headerTitle: { fontSize: 26, fontWeight: '900', color: THEME.primary },
  headerSub: { fontSize: 13, color: THEME.textLight, marginTop: 2 },
  headerActions: { flexDirection: 'row', gap: 8 },
  headerBtn: {
    width: 44, height: 44, borderRadius: 22,
    backgroundColor: '#FAE5E5', alignItems: 'center', justifyContent: 'center',
  },
  empty: { alignItems: 'center', marginTop: 80, gap: 12 },
  emptyText: { fontSize: 16, color: THEME.textLight },
  footerLoader: { paddingVertical: 20 },
});
